import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getAccountsReceivable, 
  markARPaymentComplete, 
  getEmiInstallments, 
  updateInstallmentStatus,
  getOrganizationPaymentHistory,
  getCompletedAccountsReceivable,
  toggleInstallmentPaymentStatus
} from '../services/orderService';
import { getCustomers } from '../services/customerService';
import { OrgContext } from '../contexts/OrgContext';
import { CurrencyContext } from '../contexts/CurrencyContext';
import { Check, Launch, Receipt, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import BackButton from '../components/BackButton';
import EmiInstallmentTable from '../components/EmiInstallmentTable';
import TanStackTable from '../components/TanStackTable';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import formatDateToStandard from '../utils/DateFormatter';

const AccountsReceivable = () => {
  const [loading, setLoading] = useState(true);
  const [pendingAR, setPendingAR] = useState([]);
  const [completedAR, setCompletedAR] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryPagination, setPaymentHistoryPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [completedARLoading, setCompletedARLoading] = useState(false);
  const [completedARPagination, setCompletedARPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [emiInstallments, setEmiInstallments] = useState([]);
  const { orgId } = useContext(OrgContext);
  const { currency } = useContext(CurrencyContext);
  const navigate = useNavigate();
  const { customerId } = useParams();

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$',
    CHF: 'CHF', CNY: '¥', INR: '₹', JPY: '¥', BDT: '৳',
  };
  const symbol = currencySymbols[currency] || currency;

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      // If customerId is provided in URL params, use it
      const customerIdToUse = customerId || selectedCustomer || null;
      const data = await getAccountsReceivable(customerIdToUse);
      
      // Split into pending and completed
      const pending = data.filter(item => !item.meta?.ar_complete);
      const completed = data.filter(item => item.meta?.ar_complete);
      
      // Sort pending by payment due date (ascending)
      pending.sort((a, b) => new Date(a.payment_due_date) - new Date(b.payment_due_date));
      
      // Sort completed by completion date (descending)
      completed.sort((a, b) => {
        if (!a.meta?.ar_completion_date) return 1;
        if (!b.meta?.ar_completion_date) return -1;
        return new Date(b.meta.ar_completion_date) - new Date(a.meta.ar_completion_date);
      });
      
      setPendingAR(pending);
      setCompletedAR(completed);
    } catch (error) {
      console.error('Error fetching accounts receivable:', error);
      setAlert({
        open: true,
        message: 'Failed to load accounts receivable data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmiInstallments = async () => {
    try {
      const customerId = selectedCustomer || null;
      const filters = { customerId, orderType: 'Sale' };
      const data = await getEmiInstallments(orgId, filters);
      setEmiInstallments(data);
    } catch (error) {
      console.error('Error fetching EMI installments:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch EMI installments',
        severity: 'error'
      });
    }
  };

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await getCustomers(orgId);
        setCustomers(response.data);
        
        // If customerId is in URL params, set it as selected
        if (customerId) {
          setSelectedCustomer(customerId);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };
    
    if (orgId) {
      fetchCustomers();
    }
  }, [orgId, customerId]);

  // Fetch AR data when selected customer changes
  useEffect(() => {
    if (orgId) {
      fetchData();
      fetchEmiInstallments();
      if (tabValue === 0) {
        fetchCompletedAR();
      }
    }
  }, [orgId, selectedCustomer, customerId]);

  useEffect(() => {
    if (tabValue === 0) {
      fetchCompletedAR();
    }
  }, [tabValue]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 2) {
      fetchPaymentHistory();
    }
  };

  const fetchPaymentHistory = async (options = {}) => {
    try {
      setPaymentHistoryLoading(true);
      const response = await getOrganizationPaymentHistory({
        page: options.page || paymentHistoryPagination.page,
        limit: options.limit || paymentHistoryPagination.limit,
        search: options.search,
        sortBy: options.sortBy || 'ph.payment_date',
        sortOrder: options.sortOrder || 'DESC',
        payment_type: 'ar'
      });
      
      setPaymentHistory(response.data || []);
      setPaymentHistoryPagination(response.pagination || paymentHistoryPagination);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch payment history',
        severity: 'error'
      });
    } finally {
      setPaymentHistoryLoading(false);
    }
  };

  const fetchCompletedAR = async (options = {}) => {
    try {
      setCompletedARLoading(true);
      const response = await getCompletedAccountsReceivable({
        page: options.page || completedARPagination.page,
        limit: options.limit || completedARPagination.limit,
        search: options.search,
        sortBy: options.sortBy || 'ord.order_date',
        sortOrder: options.sortOrder || 'DESC',
        customer_id: selectedCustomer || undefined
      });
      
      setCompletedAR(response.data || []);
      setCompletedARPagination(response.pagination || completedARPagination);
    } catch (error) {
      console.error('Error fetching completed AR:', error);
      setAlert({
        open: true,
        message: 'Failed to fetch completed accounts receivable',
        severity: 'error'
      });
    } finally {
      setCompletedARLoading(false);
    }
  };

  const handleOpenPaymentDialog = (order) => {
    setSelectedOrder(order);
    setPaymentAmount('');
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedOrder(null);
    setPaymentAmount('');
  };

  const calculateRemainingBalance = (order) => {
    const totalReceivable = parseFloat(order.accounts_receivable || 0);
    const paidAmount = parseFloat(order.paid_amount || 0);
    return totalReceivable - paidAmount;
  };

  const handleSubmitPayment = async () => {
    if (!selectedOrder || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    const remainingBalance = calculateRemainingBalance(selectedOrder);

    if (amount <= 0) {
      setAlert({
        open: true,
        message: 'Payment amount must be greater than 0',
        severity: 'error'
      });
      return;
    }

    if (amount > remainingBalance) {
      setAlert({
        open: true,
        message: 'Payment amount cannot exceed remaining balance',
        severity: 'error'
      });
      return;
    }

    try {
      await markARPaymentComplete(selectedOrder.id, amount);
      setAlert({
        open: true,
        message: 'Payment recorded successfully!',
        severity: 'success'
      });
      handleClosePaymentDialog();
      fetchData();
      if (tabValue === 2) {
        fetchPaymentHistory();
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setAlert({
        open: true,
        message: 'Failed to record payment',
        severity: 'error'
      });
    }
  };

  const isValidPayment = () => {
    if (!selectedOrder || !paymentAmount) return false;
    const amount = parseFloat(paymentAmount);
    const remainingBalance = calculateRemainingBalance(selectedOrder);
    return amount > 0 && amount <= remainingBalance;
  };

  const paymentHistoryColumns = [
    {
      accessorKey: 'payment_date',
      header: 'Payment Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'order_id',
      header: 'Order ID',
      cell: ({ getValue }) => (
        <Button
          variant="text"
          onClick={() => navigate(`/${orgId}/orders/${getValue()}`)}
          sx={{ 
            textTransform: 'none', 
            p: 0, 
            minWidth: 'auto',
            color: 'text.primary',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent'
            }
          }}
        >
          {getValue()}
        </Button>
      ),
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer',
      cell: ({ row }) => {
        const customerId = row.original.order?.customer_id;
        const customerName = row.original.customer?.customer_name || 
                            customers.find(c => c.id === customerId)?.customer_name || 
                            'N/A';
        return customerId ? (
          <Button
            variant="text"
            onClick={() => navigate(`/${orgId}/customers/${customerId}`)}
            sx={{ 
              textTransform: 'none', 
              p: 0, 
              minWidth: 'auto',
              color: 'text.primary',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent'
              }
            }}
          >
            {customerName}
          </Button>
        ) : customerName;
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
    {
      accessorKey: 'is_partial',
      header: 'Payment Type',
      cell: ({ getValue }) => (
        <Chip 
          label={getValue() ? 'Partial' : 'Full'} 
          size="small"
          color={getValue() ? 'warning' : 'success'}
          variant="outlined"
        />
      ),
    },
  ];

  const completedARColumns = [
    {
      accessorKey: 'id',
      header: 'Order ID',
      cell: ({ getValue }) => (
        <Button
          variant="text"
          onClick={() => navigate(`/${orgId}/orders/${getValue()}`)}
          sx={{ 
            textTransform: 'none', 
            p: 0, 
            minWidth: 'auto',
            color: 'text.primary',
            '&:hover': {
              textDecoration: 'underline',
              backgroundColor: 'transparent'
            }
          }}
        >
          {getValue()}
        </Button>
      ),
    },
    {
      accessorKey: 'customer_name',
      header: 'Customer',
      cell: ({ row }) => {
        const customerId = row.original.customer_id;
        const customerName = row.original.customer?.customer_name || 
                            customers.find(c => c.id === customerId)?.customer_name || 
                            'N/A';
        return customerId ? (
          <Button
            variant="text"
            onClick={() => navigate(`/${orgId}/customers/${customerId}`)}
            sx={{ 
              textTransform: 'none', 
              p: 0, 
              minWidth: 'auto',
              color: 'text.primary',
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent'
              }
            }}
          >
            {customerName}
          </Button>
        ) : customerName;
      },
    },
    {
      accessorKey: 'order_date',
      header: 'Order Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'payment_due_date',
      header: 'Due Date',
      cell: ({ getValue }) => formatDateToStandard(getValue()),
    },
    {
      accessorKey: 'completion_date',
      header: 'Completed Date',
      cell: ({ row }) => formatDateToStandard(row.original.meta?.ar_completion_date),
    },
    {
      accessorKey: 'accounts_receivable',
      header: 'Amount',
      cell: ({ getValue }) => <CurrencyFormatter value={getValue()} />,
    },
  ];

  // Handle customer filter change
  const handleCustomerChange = (event) => {
    const value = event.target.value;
    setSelectedCustomer(value);
    
    // Update URL to reflect selected customer
    if (value) {
      navigate(`/${orgId}/accounts-receivable/${value}`);
    } else {
      navigate(`/${orgId}/accounts-receivable`);
    }
  };

  // Handle marking AR as complete
  const handleMarkComplete = async (orderId) => {
    try {
      await markARPaymentComplete(orderId);
      setAlert({
        open: true,
        message: 'Payment marked as complete successfully',
        severity: 'success'
      });
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error marking payment as complete:', error);
      setAlert({
        open: true,
        message: 'Failed to mark payment as complete',
        severity: 'error'
      });
    }
  };

  const handleInstallmentStatusUpdate = async (installmentId, statusData) => {
    try {
      await updateInstallmentStatus(installmentId, statusData);
      setAlert({
        open: true,
        message: 'Installment status updated successfully',
        severity: 'success'
      });
      fetchEmiInstallments();
    } catch (error) {
      console.error('Error updating installment status:', error);
      setAlert({
        open: true,
        message: 'Failed to update installment status',
        severity: 'error'
      });
    }
  };

  const handleToggleInstallmentPaymentStatus = async (installmentId) => {
    try {
      await toggleInstallmentPaymentStatus(installmentId);
      setAlert({
        open: true,
        message: 'Installment status toggled successfully',
        severity: 'success'
      });
      fetchEmiInstallments();
    } catch (error) {
      console.error('Error toggling installment status:', error);
      setAlert({
        open: true,
        message: 'Failed to toggle installment status',
        severity: 'error'
      });
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    return formatDateToStandard(dateString);
  };

  // Render tables
  const renderPendingTable = () => {
    if (pendingAR.length === 0) {
      return (
        <Typography variant="body1" sx={{ py: 2, textAlign: 'center' }}>
          No pending accounts receivable found.
        </Typography>
      );
    }
    
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Order Status</TableCell> {/* Renamed from "Status" to "Order Status" */}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pendingAR.map((item) => {
              const isPastDue = new Date(item.payment_due_date) < new Date();
              
              return (
                <TableRow 
                  key={item.id}
                  sx={{ 
                    backgroundColor: isPastDue ? 'rgba(244, 67, 54, 0.1)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
                  }}
                >
                  <TableCell>
                    <Button
                      variant="text"
                      onClick={() => navigate(`/${orgId}/orders/${item.id}`)}
                      sx={{ 
                        textTransform: 'none', 
                        p: 0, 
                        minWidth: 'auto',
                        color: 'text.primary',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      {item.id}
                    </Button>
                  </TableCell>
                  <TableCell>
                    {customers.find(c => c.id === item.customer_id)?.customer_name || item.customer_id}
                  </TableCell>
                  <TableCell>{formatDate(item.order_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {formatDate(item.payment_due_date)}
                      {isPastDue && (
                        <Chip 
                          label="Past Due" 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <CurrencyFormatter value={calculateRemainingBalance(item)} />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={item.order_status} 
                      size="small"
                      color={item.order_status === 'Completed' ? 'success' : 'primary'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleOpenPaymentDialog(item)}
                      >
                        Record Payment
                      </Button>
                      <Tooltip title="Mark as Paid">
                        <IconButton 
                          color="success" 
                          onClick={() => handleMarkComplete(item.id)}
                        >
                          <Check />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Order">
                        <IconButton 
                          color="info"
                          onClick={() => navigate(`/${orgId}/orders/${item.id}`)}
                        >
                          <Launch />
                        </IconButton>
                      </Tooltip>
                      {item.order_type === 'Sale' && (
                        <Tooltip title="View Invoice">
                          <IconButton 
                            color="primary"
                            onClick={() => navigate(`/${orgId}/orders/${item.id}?invoice=true`)}
                          >
                            <Receipt />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderCompletedTable = () => {
    return (
      <Box>
        {completedARLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TanStackTable
            data={completedAR}
            columns={completedARColumns}
            enablePagination={true}
            pagination={completedARPagination}
            onPaginationChange={(newPage, newLimit) => {
              setCompletedARPagination(prev => ({...prev, page: newPage, limit: newLimit}));
              fetchCompletedAR({page: newPage, limit: newLimit});
            }}
            onSearchChange={(searchTerm) => {
              setCompletedARPagination(prev => ({...prev, page: 1}));
              fetchCompletedAR({page: 1, search: searchTerm});
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: '#1e1e1e', color: 'text.primary' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackButton />
        <Typography variant="h5" sx={{ ml: 2 }}>
          Accounts Receivable
        </Typography>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel>Filter by Customer</InputLabel>
          <Select
            value={selectedCustomer}
            onChange={handleCustomerChange}
            label="Filter by Customer"
          >
            <MenuItem value="">All Customers</MenuItem>
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.customer_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`UPFRONT PAYMENTS`} />
          <Tab label={`EMI Payments`} />
          <Tab label={`Payment History`} />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Unpaid Accounts Receivable
              </Typography>
              {renderPendingTable()}
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Paid Accounts Receivable
              </Typography>
              {renderCompletedTable()}
            </Box>
          )}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                EMI Installments
              </Typography>
              {emiInstallments.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No EMI installments found for accounts receivable
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {Object.entries(
                    emiInstallments.reduce((acc, installment) => {
                      const orderId = installment.order.id;
                      if (!acc[orderId]) {
                        acc[orderId] = {
                          order: installment.order,
                          installments: []
                        };
                      }
                      acc[orderId].installments.push(installment);
                      return acc;
                    }, {})
                  ).map(([orderId, { order, installments }]) => (
                    <Accordion key={orderId} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                          <Typography 
                            variant="subtitle1" 
                            component="span" 
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => {
                              const customerId = order.customer_id;
                              if (customerId) {
                                navigate(`/${orgId}/customers/${customerId}`);
                              }
                            }}
                          >
                            Order #{order.id} - {order.customer?.customer_name || customers.find(c => c.id === order.customer_id)?.customer_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {installments.length} installments
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <EmiInstallmentTable
                          installments={installments}
                          onStatusUpdate={handleInstallmentStatusUpdate}
                          onTogglePaymentStatus={handleToggleInstallmentPaymentStatus}
                          currencySymbol={symbol}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          )}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Payment History
              </Typography>
              {paymentHistoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TanStackTable
                  data={paymentHistory}
                  columns={paymentHistoryColumns}
                  enablePagination={true}
                  pagination={paymentHistoryPagination}
                  onPaginationChange={(newPage, newLimit) => {
                    setPaymentHistoryPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                    fetchPaymentHistory({page: newPage, limit: newLimit});
                  }}
                  onSearchChange={(searchTerm) => {
                    setPaymentHistoryPagination(prev => ({...prev, page: 1}));
                    fetchPaymentHistory({page: 1, search: searchTerm});
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      )}
      
      <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Order ID: {selectedOrder.id}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Total Receivable: <CurrencyFormatter value={selectedOrder.accounts_receivable} />
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Remaining Balance: <CurrencyFormatter value={calculateRemainingBalance(selectedOrder)} />
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Payment Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                error={paymentAmount && !isValidPayment()}
                helperText={
                  paymentAmount && !isValidPayment()
                    ? `Amount must be between 0 and ${calculateRemainingBalance(selectedOrder).toFixed(2)}`
                    : `Enter amount between 0 and ${calculateRemainingBalance(selectedOrder).toFixed(2)}`
                }
                inputProps={{
                  min: 0,
                  max: calculateRemainingBalance(selectedOrder),
                  step: 0.01
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmitPayment} 
            variant="contained"
            disabled={!isValidPayment()}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert 
          onClose={() => setAlert({ ...alert, open: false })} 
          severity={alert.severity}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AccountsReceivable;
